import React, {useState} from 'react'
import {toIsoDate} from '../../utils/calendarDates.ts'

// Building blocks of the "Asignar TripBook" dialog (AssignInvitationCodeDialog)
// that don't depend on its state: the design system's floating-label field and
// the range calendar's day cell (date helpers live in utils/calendarDates.ts).

// The design system's floating-label field: label rests inside the 58px box and
// rises once the field is focused or filled.
export const FloatingField: React.FC<{
    label: string
    value: string
    type?: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onFocus?: () => void
}> = ({label, value, type = 'text', onChange, onFocus}) => {
    const [isFocused, setIsFocused] = useState(false)
    const isFloated = isFocused || value.length > 0
    return (
        <div className={`relative w-full h-[58px] px-3.5 flex items-end bg-white rounded-xl border-[1.5px] ${isFocused ? 'border-neutralBlack-600 shadow-none' : 'border-transparent shadow-[0_0_5px_rgba(0,0,0,0.15)]'}`}>
            <span className={`absolute left-[15px] font-medium text-neutralBlack-500 pointer-events-none transition-all duration-200 ease-out ${isFloated ? 'top-2 text-xs' : 'top-1/2 -translate-y-1/2 text-base'}`}>
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                onFocus={() => {setIsFocused(true); onFocus?.()}}
                onBlur={() => setIsFocused(false)}
                className="w-full flex-1 bg-transparent border-none outline-none text-base text-neutralBlack-900 pt-[9px] pb-[11px]"
            />
        </div>
    )
}

export const CalendarDay: React.FC<{
    value: string
    startDate: string
    endDate: string
    hoverDate: string
    onPick: (value: string) => void
    onHover: () => void
}> = ({value, startDate, endDate, hoverDate, onPick, onHover}) => {
    const rangeEnd = endDate || (startDate && hoverDate > startDate ? hoverDate : '')
    const isStart = value === startDate
    const isEnd = value === endDate
    const isInside = !!startDate && !!rangeEnd && value > startDate && value < rangeEnd
    const isToday = value === toIsoDate(new Date())

    const edge = isStart && rangeEnd ? 'rounded-l-[10px] rounded-r-none' : isEnd ? 'rounded-l-none rounded-r-[10px]' : 'rounded-[10px]'
    const tone = isStart || isEnd
        ? 'bg-neutralBlack-900 text-white font-bold'
        : isInside
            ? 'bg-[#EEF8F2] text-[#16603F] rounded-none'
            : `text-neutralBlack-900 hover:bg-neutralBlack-100 ${isToday ? 'shadow-[inset_0_0_0_1.5px_theme(colors.neutralBlack.300)]' : ''}`

    return (
        <button type="button" onClick={() => onPick(value)} onMouseEnter={onHover}
                className={`h-[38px] text-[13.5px] font-medium ${edge} ${tone}`}>
            {Number(value.slice(8, 10))}
        </button>
    )
}

export const GiftIcon: React.FC = () => (
    <svg viewBox="12 12 74 74" className="w-[26px] h-[26px] flex-none" fill="currentColor" aria-hidden="true">
        <path d="M18,46V78a4,4,0,0,0,8,0V46a4,4,0,0,0-8,0Z"/>
        <path d="M66,30.49v-.62A11.88,11.88,0,0,0,54.13,18H53a8,8,0,0,0-6.77,3.73l-12,19A8,8,0,0,0,33,45V72.29A9.71,9.71,0,0,0,42.71,82H62.42a12,12,0,0,0,10.94-7.08l7.36-16.35a14.63,14.63,0,0,0,.76-9.87A14.82,14.82,0,0,0,67.13,38H64.41l1.19-4.45A11.91,11.91,0,0,0,66,30.49ZM67.13,46a6.82,6.82,0,0,1,6.64,4.83,6.59,6.59,0,0,1-.34,4.46L66.06,71.64A4,4,0,0,1,62.42,74H42.71A1.71,1.71,0,0,1,41,72.29V45L53,26h1.14A3.87,3.87,0,0,1,58,29.87v.63a3.9,3.9,0,0,1-.13,1l-2.72,10.2A3.43,3.43,0,0,0,58.46,46Z"/>
    </svg>
)
